import pointsTranslation from '@r/translations/points-translation.ts';
import { useTranslation } from 'react-i18next';
import { useUser } from '@r/api/auth.tsx';
import Marquee from '@ui/marquee.tsx';
import {
  getRouteApi,
  notFound,
  type RouteIds,
  useLocation,
  useNavigate,
  useRouter
} from '@tanstack/react-router';
import { CartInterface } from '@client/components/cart/cart-interface.tsx';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { useApiShopsIdGet, useApiShopsShopIdproductsIdGet } from '@r/api/generated/client.ts';
import { Product } from '@client/components/shop/product.tsx';
import { BreadcrumbNavigation } from '@client/components/breadcrumb/breadcrumb-nav.tsx';
import { BreadcrumbSkeleton } from '@client/components/skeleton/breadcrumb-skeleton.tsx';
import { ProductSkeleton } from '@client/components/skeleton/product-skeleton.tsx';
import { CartInterfaceSkeleton } from '@client/components/skeleton/cart-interface-skeleton.tsx';
import { Button } from '@ui/button.tsx';
import { useBalance } from '@r/context/balance-context.ts';
import type { SearchParams } from '@r/types/index.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { isOrvalNotFoundError } from '@r/api/orval-error-helper.ts';

const ProductPage = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const navigate = useNavigate();

  const location = useLocation();
  const { previousRoute } = location.search as SearchParams;

  const user = useUser().data;
  const workspaces = UseWorkspace(user);
  const { balance } = useBalance();

  const { shopId, id: productId } = getRouteApi(
    '/shops/$shopId/products/$id' as RouteIds<typeof router.routeTree>
  ).useParams() as { shopId: string; id: string };

  const isDataAvailable = !!shopId && !!workspaces.workspace?.id && !!user?.id;

  const shopQuery = useApiShopsIdGet(shopId, {
    query: {
      queryKey: ['shop_id', shopId, workspaces.workspace?.id, user?.id],
      enabled: isDataAvailable
    }
  });
  useOrvalQueryErrorToast(shopQuery, t('toast.error.label'), undefined, { skipNotFound: true });

  const {
    data: shop,
    isSuccess: isShopSuccess,
    isPending: isShopPending,
    isError: isShopError
  } = shopQuery;

  const productQuery = useApiShopsShopIdproductsIdGet(shopId, productId, {
    query: {
      queryKey: ['product_id', productId, workspaces.workspace?.id, user?.id],
      enabled: !!workspaces.workspace?.id
    }
  });
  useOrvalQueryErrorToast(productQuery, t('toast.error.label'), undefined, { skipNotFound: true });

  const {
    data: product,
    isSuccess: isProductSuccess,
    isPending: isProductPending,
    isError: isProductError
  } = productQuery;

  if (shopQuery.isError && isOrvalNotFoundError(shopQuery.error)) {
    throw notFound();
  }

  if (productQuery.isError && isOrvalNotFoundError(productQuery.error)) {
    throw notFound();
  }

  const breadcrumbItems = [
    { label: 'Boutiques', to: '/shops' },
    {
      label: shop?.displayName || '',
      to: '/shops/$shopId/products',
      params: { shopId: shop?.id.toString() || '' }
    },
    { label: product?.name || '', isCurrentPage: true }
  ];

  const handleBack = () => {
    const isFromProductsList = previousRoute?.includes(`/shops/${shopId}/products`);
    const hasHistory = window.history.length > 1;

    if (isFromProductsList && hasHistory) {
      router.history.back();
    } else {
      navigate({
        to: '/shops/$shopId/products',
        params: { shopId },
        replace: true
      }).catch((error) => {
        console.error('Error navigating back to products list:', error);
      });
    }
  };

  return (
    <>
      {user && (
        <div className="flex sticky top-0 z-10 justify-center items-center h-5 text-sm text-center rounded-none border-0 lg:hidden bg-primary text-background">
          {t('shop.product.your.balance')}
          {pointsTranslation(t, balance, true)}
        </div>
      )}

      <div className="pt-10 pb-7 space-y-5 app-shell sm:px-7">
        {isShopSuccess && isProductSuccess && (
          <BreadcrumbNavigation items={breadcrumbItems} className="px-5 sm:px-0" />
        )}
        {(isShopPending || isShopError || isProductPending || isProductError) && (
          <BreadcrumbSkeleton />
        )}

        <div className="flex flex-wrap space-y-5 w-full grow xl:flex-nowrap xl:space-y-0 xl:space-x-5">
          <div className="mb-10 space-y-5 w-full xl:w-9/12">
            {isProductSuccess && <Product product={product} />}
            {(isProductPending || isProductError) && <ProductSkeleton />}

            <Button
              variant="outline"
              rounded="full"
              onClick={handleBack}
              className="relative z-30 ml-5 sm:ml-0"
            >
              {t('common.back.catalog')}
            </Button>
          </div>

          <div className="sticky bottom-0 z-20 self-start w-full sm:bottom-7 xl:static xl:w-3/12">
            {isProductSuccess && isShopSuccess && <CartInterface product={product} shop={shop} />}
            {(isProductPending || isProductError || isShopPending || isShopError) && (
              <CartInterfaceSkeleton />
            )}
          </div>
        </div>
      </div>

      {isShopSuccess && <Marquee text={shop.displayName} className="hidden xl:block" />}
    </>
  );
};

export { ProductPage };
